import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import { fetchConnectedPages } from '@services/getConnectedPages';
import { fetchAdAccounts } from '@services/ml/adAccountsService';
import { notify } from '@utils/notify';
import Header from '@components/Header/Header';
import SelectComponent from '@components/SelectComponent';
import ButtonComponent from '@components/ButtonComponent';
import TextFieldComponent from '@components/TextFieldComponent';
import SheetErrorModal from './SheetErrorModal';
import SheetAdSetSelectionModal from './SheetAdSetCreationModal';
import SheetSummaryModal from './SheetSummaryModal';
import getSheetValidation from '@services/sheetValidation';
import { fetchFreshFacebookCampaigns } from "../../services/getAllCampaigns";
import fetchFreshFacebookAdsets from "@services/getAllAdSets";
import SheetProcessingLoader from './SheetProcessingLoader';
import { Loader } from '@components/Loader';
import TooltipComponent from '@components/TooltipComponent/TooltipComponent';
import AdPostingValidation from "@components/AdPostingValidation/AdPostingValidation";
import { fetchAdsLaunchValidationErrors } from "@services/validateFbAdsLaunch";



const SheetAdLauncher = () => {
  // Tooltip state for refresh icon
  const [showTooltip, setShowTooltip] = useState(false);
  // Loading states for refresh button
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingAdSets, setLoadingAdSets] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  // Loading state for Facebook/Instagram refresh
  const [isRefreshingPages, setIsRefreshingPages] = useState(false);
  // const navigate = useNavigate();
  // State for storing API response data
  const [existingAdsets, setExistingAdsets] = useState({});
  const [campaignsMap, setCampaignsMap] = useState({});
  // New state for fresh adsets and adsets to create
  const [freshAdsetsData, setFreshAdsetsData] = useState(null);
  const [adsetsToCreateData, setAdsetsToCreateData] = useState(null);
  const [adCountsSummaryData, setAdCountsSummaryData] = useState(null);

  // Facebook/Instagram pages state
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramPages, setInstagramPages] = useState([]);
  const [selectedFB, setSelectedFB] = useState(null);
  const [selectedIG, setSelectedIG] = useState(null);
  // Ad accounts state
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [isLoadingAdAccounts, setIsLoadingAdAccounts] = useState(true);

  // Checkbox state for 'One ad per ad set'
  const [googleSheetLink, setGoogleSheetLink] = useState('');
  // State for column letter next to ending row
  const [columnLetter, setColumnLetter] = useState('J');
  const [startingRow, setStartingRow] = useState();
  const [endingRow, setEndingRow] = useState();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorData, setErrorData] = useState({ type: '', message: '', details: '' });
  // Loading state for sheet validation API
  const [isLoadingSheetValidation, setIsLoadingSheetValidation] = useState(false);
  const [showAdSetCreationModal, setShowAdSetCreationModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);


  const userId = localStorage.getItem("userId");
  // Get adAccountId from selectedAdAccount in adAccounts array
  const selectedAccountObj = adAccounts.find(acc => (acc.fbAdAccountId || acc.id) === selectedAdAccount);
  const adAccountId = selectedAccountObj?.fbAdAccountId || selectedAccountObj?.id || '';

  // Fetch connected pages on userId change
  useEffect(() => {
    async function getConnectedPagesData() {
      try {
        const response = await fetchConnectedPages({ user_id: userId, force_refresh: false });
        if (response?.data?.data?.facebook_pages) {
          setFacebookPages(response.data.data.facebook_pages);
        }
        if (response?.data?.data?.instagram_pages) {
          setInstagramPages(response.data.data.instagram_pages);
        }
      } catch {
        setFacebookPages([]);
        setInstagramPages([]);
      }
    }
    if (userId) getConnectedPagesData();
  }, [userId]);

  // Set default FB/IG page on load
  useEffect(() => {
    if (facebookPages && facebookPages.length > 0 && !selectedFB) {
      const defaultFbPage = facebookPages[0];
      setSelectedFB(defaultFbPage.page_id);
      // Auto-select related Instagram page
      const relatedInstagramPage = instagramPages.find(instaPage => instaPage.page_id === defaultFbPage.page_id);
      if (relatedInstagramPage) {
        setSelectedIG(relatedInstagramPage.insta_id);
      }
    }
  }, [facebookPages, instagramPages]);

  // Handler for refreshing campaigns and adsets
  const handleRefreshCampaignsAndAdSets = async () => {
    setLoadingCampaigns(true);
    setLoadingAdSets(true);
    try {
      // Refresh campaigns
      const campaignResponse = await fetchFreshFacebookCampaigns({
        user_id: userId,
        ad_account_id: selectedAdAccount,
        force_refresh: true
      });
      setCampaigns(campaignResponse.data);
    } catch (err) {
      console.log(err);
      setCampaigns([]);
    }
    setLoadingCampaigns(false);
    try {
      // Refresh all adsets for account
      const allAdsetsResponse = await fetchFreshFacebookAdsets({
        user_id: userId,
        ad_account_id: selectedAdAccount,
        force_refresh: true
      });
      setFreshAdsetsData(allAdsetsResponse.data);
    } catch (err) {
      console.log(err);
      setFreshAdsetsData([]);
    }
    setLoadingAdSets(false);
  };

  // Handler for refreshing both ad accounts and connected pages
  const handleRefreshAdAccountAndPages = async () => {
    if (!userId) return;
    setIsLoadingAdAccounts(true);
    setIsRefreshingPages(true);
    try {
      // Refresh ad accounts
      const data = await fetchAdAccounts(userId, true);
      const accounts = Array.isArray(data) ? data : data?.data || [];
      setAdAccounts(accounts);
    } catch {
      setAdAccounts([]);
    } finally {
      setIsLoadingAdAccounts(false);
    }
    try {
      // Refresh connected pages
      const response = await fetchConnectedPages({ user_id: userId, force_refresh: true });
      if (response?.data?.data?.facebook_pages) {
        setFacebookPages(response.data.data.facebook_pages);
      } else {
        setFacebookPages([]);
      }
      if (response?.data?.data?.instagram_pages) {
        setInstagramPages(response.data.data.instagram_pages);
      } else {
        setInstagramPages([]);
      }
    } catch {
      setFacebookPages([]);
      setInstagramPages([]);
    } finally {
      setIsRefreshingPages(false);
    }
  };

  // Handle Facebook selection change and update Instagram accordingly
  const handleFacebookChange = (e) => {
    const newFbValue = e.target.value;
    setSelectedFB(newFbValue);
    // Auto-select related Instagram page
    const relatedInstagramPage = instagramPages.find(instaPage => instaPage.page_id === newFbValue);
    if (relatedInstagramPage) {
      setSelectedIG(relatedInstagramPage.insta_id);
    } else {
      setSelectedIG("");
    }
  };

  // Fetch ad accounts on mount
  useEffect(() => {
    async function fetchAccounts() {
      if (!userId) return;
      setIsLoadingAdAccounts(true);
      try {
        const data = await fetchAdAccounts(userId, false);
        const accounts = Array.isArray(data) ? data : data?.data || [];
        setAdAccounts(accounts);
        // Prefill default ad account
        if (accounts.length > 0) {
          const defaultAcc = accounts.find(a => a.isDefault) || accounts[0];
          setSelectedAdAccount(defaultAcc.fbAdAccountId || defaultAcc.id || '');
        }
      } catch {
        setAdAccounts([]);
      } finally {
        setIsLoadingAdAccounts(false);
      }
    }
    fetchAccounts();
  }, [userId]);

  // Google Sheet URL validation
  const isValidGoogleSheetUrl = url => {
    return /^https:\/\/docs\.google\.com\/spreadsheets\/.+/.test(url);
  };

  // Centralized error handling for sheet link and rows
  const handleSheetValidation = (onSuccess) => {
    if (!isValidGoogleSheetUrl(googleSheetLink)) {
      notify('Please enter a valid Google Sheet URL.', 'error');
      return;
    }
    const rowError = getRowError(startingRow, endingRow);
    if (rowError) {
      notify(rowError, 'error');
      return;
    }
    onSuccess();
  };

  // Utility to get array of row numbers from start to end (inclusive)
  const getRowsToProcess = (start, end) => {
    const s = Number(start);
    const e = Number(end);
    if (!s || !e || s < 1 || e < 1 || e < s) return [];
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  };

  // Function to call getSheetValidation API and log response
  const handleLoadSheet = async (adsetValidate = true) => {
    setIsLoadingSheetValidation(true);
    try {
      // Ensure startingRow and endingRow are numbers
      const start = Number(startingRow);
      const end = Number(endingRow);
      const rowsToProcess = getRowsToProcess(start, end);
      const payload = {
        user_id: userId,
        ad_account_id: adAccountId,
        sheet_url: googleSheetLink,
        rows_to_process: rowsToProcess
      };
      const response = await getSheetValidation(payload);

      // Handle success response
      if (response && response[0] === true) {
        const data = response[1];

        if (adsetValidate) {
          // make api call to validate adsets
          const adsetValidationPayload = {
            mode: "sheet_adsets_validate",
            payload: {
              user_id: userId,
              ad_account_id: adAccountId,
              sheet_url: googleSheetLink,
              rows_to_process: rowsToProcess,
              existing_adsets: data.existing_adsets,
              ad_counts_summary: data.ad_counts_summary
            }
          };
          const errors = await checkForValidationErrors(adsetValidationPayload);
          if (errors.length > 0) {
            setValidationErrors(errors);
            setIsValidationModalOpen(true);
            setIsLoadingSheetValidation(false);
            return;
          }
        }

        
        setExistingAdsets(data.existing_adsets);
        setCampaignsMap(data.campaigns_map);
        const adsetsToCreate = data.adsets_to_create;
        const adCountsSummary = data.ad_counts_summary;
        setAdCountsSummaryData(adCountsSummary);
        if (adsetsToCreate && Object.keys(adsetsToCreate).length > 0) {
          try {
            const freshAdsetsResponse = await fetchFreshFacebookAdsets({
              user_id: userId,
              ad_account_id: adAccountId,
              force_refresh: false
            });
            setFreshAdsetsData(freshAdsetsResponse?.data);
            setAdsetsToCreateData(adsetsToCreate);
          } catch (err) {
            notify("Error fetching fresh Facebook adsets", err)
            console.error('Error fetching fresh Facebook adsets:', err);
          }
          setShowAdSetCreationModal(true);
        } else {
          setShowSummaryModal(true);
        }
      }
    } catch (error) {
      console.error('getSheetValidation error:', error);
      setErrorData({
        type: 'Validation Error',
        message: error?.message,
        details: error?.response?.data?.error
      });
      setShowErrorModal(true);
    } finally {
      setIsLoadingSheetValidation(false);
    }
  };

  const checkForValidationErrors = async (payload) => {
    try {
      setIsLoadingSheetValidation(true);
      const response = await fetchAdsLaunchValidationErrors(payload);

      if (response.data && response.data.validation && !response.data.validation.is_valid) {
        const errors = response.data.validation.error.map((error, index) => ({
          id: index + 1,
          error: error,
          errorDetails: response.data.validation.error_details[index] || "",
          action: response.data.validation.actions[index] || ""
        }));
        return errors;
      }

      return [];
    } catch (error) {
      console.error("Error validating adsets launch:", error);
      notify("Error validating adsets launch", "error");
      return [];
    } finally {
      setIsLoadingSheetValidation(false);
    }
  };

  const handleAdsetValidationEditAds = () => {
    setIsValidationModalOpen(false);
    // Proceed with ad launching, ignoring validation errors
    handleLoadSheet(false); // Don't validate again
  };

  const handleAdsetValidationResolve = async () => {
    setIsValidationModalOpen(false);
    // setIsLoadingSheetValidation(true);    
    handleLoadSheet(true); // Validate again with true
  };

  // function to get row error
  const getRowError = (start, end) => {
    const s = Number(start);
    const e = Number(end);
    if (!s || !e || s < 1 || e < 1) {
      return 'Rows must be positive numbers.';
    }
    if (e < s) {
      return 'Ending row cannot be less than starting row.';
    }
    return null;
  };


  return (
    <div className="flex flex-col min-h-screen bg-gray-50 sheet-ad-launcher-container">
      <Header />
      {isLoadingSheetValidation ? (
        <SheetProcessingLoader />
      ) : (
        <>
          <main className="flex-1 flex flex-col items-start mt-[68px] pb-[90px] mx-[40px] overflow-y-auto overflow-x-hidden">
            {(loadingCampaigns || loadingAdSets) && (
              <div className="absolute inset-0 flex items-center justify-center z-50 backdrop-blur-sm">
                <Loader />
              </div>
            )}
            <section className="w-full mx-auto mt-10 bg-gray-100 rounded-xl">
              <div className="flex flex-col gap-5 p-5 transition-all duration-300 bg-white border border-gray-300 rounded-lg shadow-sm">
                {/* Title and Platform Dropdowns */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <div className="flex items-end">
                      <img src="/gSheet.svg" alt="Google Sheet" className="w-6 h-6 mr-2" />
                      <div
                        className="font-inter font-medium text-[18px] leading-[1.4] text-gray-900"
                        style={{ fontWeight: 500 }}
                      >
                        Google Sheet Ads Launcher
                      </div>
                    </div>
                    <p
                      className="font-inter text-[16px] leading-[1.4] text-[#6B7280] mt-1 ml-2"
                      style={{ fontWeight: 400 }}
                    >
                      Load your Google Sheets to launch ads in bulk.
                    </p>
                  </div>
                  <div className="flex gap-4 items-end">
                    {/* Ad Account Refresh Icon to the left of Selector */}
                    <div className="flex items-center relative">
                      <div
                        className="relative"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onFocus={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                        tabIndex={-1}
                      >
                        <img
                          src="/refresh-gray.svg"
                          alt="Refresh Ad Account and Pages"
                          className={`w-5 h-5 mr-3 transition-transform duration-200 hover:rotate-90 ${(isLoadingAdAccounts || isRefreshingPages) ? 'animate-spin opacity-50' : ''}`}
                          style={{ cursor: (isLoadingAdAccounts || isRefreshingPages) ? 'not-allowed' : 'pointer', opacity: 0.7 }}
                          onClick={(isLoadingAdAccounts || isRefreshingPages) ? undefined : handleRefreshAdAccountAndPages}
                          tabIndex={0}
                          role="button"
                          aria-label="Refresh Ad Account and Pages"
                          onFocus={() => setShowTooltip(true)}
                          onBlur={() => setShowTooltip(false)}
                        />
                        {showTooltip && (
                          <TooltipComponent
                            text="Refresh ad account, facebook page and instagram page"
                            position="left-[-125px] bottom-[25px]"
                            width="full"
                            height="32px"
                          />
                        )}
                      </div>
                      <SelectComponent
                        value={selectedAdAccount}
                        onChange={e => setSelectedAdAccount(e.target.value)}
                        options={adAccounts.map(acc => ({
                          value: acc.fbAdAccountId || acc.id,
                          label: acc.fbAdAccountName
                        }))}
                        icon="/pointer.svg"
                        placeholder={isLoadingAdAccounts ? 'Loading...' : 'Select Ad Account'}
                        width="180px"
                        label={null}
                        disabled={isLoadingAdAccounts || adAccounts.length === 0}
                      />
                    </div>
                    {/* Facebook Page Selector (no refresh icon) */}
                    <div className="flex flex-col items-start">
                      <SelectComponent
                        value={selectedFB || ''}
                        onChange={handleFacebookChange}
                        options={facebookPages.map(page => ({
                          value: page.page_id,
                          label: page.name
                        }))}
                        icon="/facebook.svg"
                        placeholder="Facebook"
                        width="180px"
                        label={null}
                        disabled={facebookPages.length === 0}
                      />
                    </div>
                    {/* Instagram Page Selector */}
                    <SelectComponent
                      value={selectedIG || ''}
                      onChange={e => setSelectedIG(e.target.value)}
                      options={instagramPages
                        .filter(instaPage => instaPage.page_id === selectedFB)
                        .map(instaPage => ({
                          value: instaPage.insta_id,
                          label: instaPage.insta_page_name
                        }))}
                      icon="/instagram.svg"
                      placeholder="Instagram"
                      width="180px"
                      label={null}
                      disabled={instagramPages.filter(instaPage => instaPage.page_id === selectedFB).length === 0}
                    />
                  </div>
                </div>
                <hr className="border-gray-200" />
                {/* Google Sheet Link, Row Range, and Instructions */}
                <div className="flex flex-col gap-4 p-5 border border-gray-200 rounded-lg bg-gray-50">
                  <div>
                    <TextFieldComponent
                      label={<span className="font-inter font-medium text-[14px] leading-[1.2] text-[#1F2937]" style={{ fontWeight: 500 }}>Google Sheet Link</span>}
                      placeholder="Paste here"
                      value={googleSheetLink}
                      onChange={e => setGoogleSheetLink(e.target.value)}
                      width="100%"
                      icon="/linkGray.svg"
                      inputClassName="font-inter text-[16px] leading-[1.4] text-center font-normal"
                    />
                    <div className="flex gap-4 mt-4">
                      <div className="flex-1">
                        <TextFieldComponent
                          label={<span className="font-inter font-medium text-[14px] leading-[1.2] text-[#1F2937]" style={{ fontWeight: 500 }}>Enter starting row</span>}
                          placeholder="From row"
                          value={startingRow}
                          onChange={e => setStartingRow(e.target.value)}
                          width="100%"
                          icon="/inc.svg"
                          type="number"
                          inputClassName="font-inter text-[16px] leading-[1.4] text-center font-normal"
                        />
                      </div>
                      <div className="flex-1">
                        <TextFieldComponent
                          label={<span className="font-inter font-medium text-[14px] leading-[1.2] text-[#1F2937]" style={{ fontWeight: 500 }}>Enter ending row</span>}
                          placeholder="Till row"
                          value={endingRow}
                          onChange={e => setEndingRow(e.target.value)}
                          width="100%"
                          icon="/dec.svg"
                          type="number"
                          inputClassName="font-inter text-[16px] leading-[1.4] text-center font-normal"
                        />
                      </div>
                      <div className="flex-1">
                        <TextFieldComponent
                          label={<span className="font-inter font-medium text-[14px] leading-[1.2] text-[#1F2937]" style={{ fontWeight: 500 }}>Status Column</span>}
                          placeholder="Status Column"
                          value={columnLetter}
                          onChange={e => setColumnLetter(e.target.value)}
                          width="100%"
                          icon="/alphabet.svg"
                          inputClassName="font-inter text-[16px] leading-[1.4] text-center font-normal"
                        />
                      </div>
                    </div>
                    {/* Checkbox for one ad per ad set removed */}
                  </div>
                  <div className="mt-4">
                    <p className="font-inter text-[13px] leading-[1.4] text-[#6B7280] font-normal mb-3">
                      Notes:
                    </p>
                    <ul className="ml-4 space-y-1 text-[13px] leading-[1.4] text-[#6B7280] font-inter font-normal list-disc">
                      <li>Use our templates for 
                      <a href="https://docs.google.com/spreadsheets/d/1ts7tPEx03pEpCRfx-10-JG9ie5wFV1eadUaU0Awp5Ng/edit?gid=0#gid=0" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">App Campaigns</a> or 
                      <a href="https://docs.google.com/spreadsheets/d/1cnHcpBOkP536txR6X8_RVdNO0tWdBQpPlpnUBqtENH8/edit?gid=0#gid=0" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">Non-App Campaigns</a>. </li>
                      <li>Make sure the sheet and the creatives are  publicly accessible or shared with write permissions to this email id: <span className="italic"> testdriveank1@halogen-country-393311.iam.gserviceaccount.com</span></li>
                    </ul>
                  </div>
                </div>
                {/* Load Sheet Button */}
                <div className="flex justify-end gap-4">
                  {/* Refresh Ad Sets and Campaigns button */}
                  <ButtonComponent
                    variant="secondary"
                    onClick={handleRefreshCampaignsAndAdSets}
                    disabled={loadingCampaigns || loadingAdSets}
                  >
                    {(loadingCampaigns || loadingAdSets) ? 'Refreshing...' : 'Refresh Ad Sets and Campaigns'}
                  </ButtonComponent>
                  <ButtonComponent
                    variant="primary"
                    disabled={!googleSheetLink || !startingRow || !endingRow || isLoadingSheetValidation}
                    onClick={() => handleSheetValidation(handleLoadSheet)}
                  >
                    {isLoadingSheetValidation ? 'Loading...' : 'Load Sheet'}
                  </ButtonComponent>
                </div>
              </div>
            </section>
          </main>
          <SheetErrorModal open={showErrorModal} onClose={() => setShowErrorModal(false)} error={errorData} />
          {isValidationModalOpen && validationErrors.length > 0 && (
            <AdPostingValidation
              isOpen={isValidationModalOpen}
              onClose={() => setIsValidationModalOpen(false)}
              errors={validationErrors}
              onEditAds={handleAdsetValidationEditAds}
              onResolveErrors={handleAdsetValidationResolve}
            />
          )}
          <SheetAdSetSelectionModal
            open={showAdSetCreationModal}
            onClose={() => setShowAdSetCreationModal(false)}
            freshAdsetsData={freshAdsetsData}
            adsetsToCreate={adsetsToCreateData}
            campaignsMap={campaignsMap}
            existingAdsets={existingAdsets}
            setShowSummaryModal={setShowSummaryModal}
            setAdCountsSummaryData={setAdCountsSummaryData}
            setExistingAdsets={setExistingAdsets}
            adCountsSummary={adCountsSummaryData}
            userId={userId}
            adAccountId={adAccountId}
          />
          <SheetSummaryModal
            open={showSummaryModal}
            onClose={() => setShowSummaryModal(false)}
            adCountsSummary={adCountsSummaryData}
            userId={userId}
            adsetsToCreate={adsetsToCreateData}
            adAccountId={adAccountId}
            rowsToProcess={getRowsToProcess(startingRow, endingRow)}
            selectedFB={selectedFB}
            selectedIG={selectedIG}
            sheetUrl={googleSheetLink}
            existingAdsets={existingAdsets}
            columnLetter={columnLetter}
          />
        </>
      )}
    </div>
  );
};

export default SheetAdLauncher;